/**
 * Convert a local datetime (in building timezone) to UTC ISO string
 * @param localDatetime - datetime-local format string (e.g., "2026-05-15T15:00")
 * @param buildingTimezone - IANA timezone string (e.g., "America/Los_Angeles")
 * @returns ISO string in UTC (e.g., "2026-05-15T22:00:00.000Z")
 */
export function localToUTC(localDatetime: string, buildingTimezone: string): string {
  // Parse input: "2026-05-15T09:09" or "2026-05-15T09:09:00" → components
  const [datePart, timePart] = localDatetime.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const timeParts = timePart.split(':').map(Number);
  const hour = timeParts[0];
  const minute = timeParts[1];
  const second = timeParts[2] ?? 0;

  // Strategy:
  //   1. Construct a Date as if the input were already UTC. This gives a
  //      "guess" UTC ms that's wrong by the building timezone's offset.
  //   2. Format that guess back in the building timezone to see what
  //      hour-of-day comes out. The difference between (input) and
  //      (formatted) is exactly the timezone offset we need to subtract.
  //   3. Iterate twice — the offset can differ at the guess vs. the
  //      corrected UTC point near DST transitions. Two passes converge.
  //
  // hourCycle: 'h23' is critical — it forces the 0–23 cycle. The previous
  // implementation used hour12: false with locale en-US, which (in modern
  // ICU) returns '24' for midnight in some browsers and '00' in others.
  // h23 is the explicit standard and is well-defined across engines.
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, second);

  for (let iter = 0; iter < 2; iter++) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: buildingTimezone,
      year:   'numeric',
      month:  '2-digit',
      day:    '2-digit',
      hour:   '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(utcMs));
    const get = (t: string) => parseInt(parts.find((p) => p.type === t)!.value);
    const tzAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    const offsetMs = utcMs - tzAsUtc;
    utcMs = Date.UTC(year, month - 1, day, hour, minute, second) + offsetMs;
  }

  return new Date(utcMs).toISOString();
}
